/**
 * CLOUD-715. A workspace switch re-runs federation through the Business Cloud
 * IDP, and that IDP answers from its own browser session. If the session belongs
 * to another account, Ari Cloud comes back as that account. We record who started
 * the switch and refuse the session if somebody else comes back.
 *
 * Business Cloud keys a user record on (email, workspace), so the JWT sub changes
 * on every legitimate switch. Email is the only stable identity here.
 */

// Tab-scoped markers. sessionStorage survives the redirect chain in this tab,
// cannot reach a login in another tab, and dies with the tab.
export const WORKSPACE_SWITCH_PIN_KEY = 'ariCloud.workspaceSwitchPin';
export const WORKSPACE_SWITCH_BLOCKED_KEY = 'ariCloud.workspaceSwitchBlocked';

/** A switch takes seconds. An older pin belongs to an abandoned flow. */
const PIN_MAX_AGE_MS = 5 * 60 * 1000;

export type WorkspaceSwitchPin = {
  email: string;
  workspaceId: string;
  startedAt: number;
};

export type BlockedWorkspaceSwitch = {
  expectedEmail: string;
  actualEmail: string | null;
};

function normalizeEmail(email: string | null | undefined): string | null {
  const trimmed = email?.trim().toLowerCase();
  return trimmed ? trimmed : null;
}

function parse<T>(raw: string | null): Partial<T> | null {
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as Partial<T>;
  } catch {
    return null;
  }
}

/**
 * Records who is switching. Without an email there is nothing to compare later,
 * so no pin is written and the switch stays unguarded — better than refusing
 * every legitimate switch.
 */
export function pinWorkspaceSwitch(email: string | null | undefined, workspaceId: string): void {
  const pinned = normalizeEmail(email);
  if (!pinned) return;
  const pin: WorkspaceSwitchPin = { email: pinned, workspaceId, startedAt: Date.now() };
  sessionStorage.setItem(WORKSPACE_SWITCH_PIN_KEY, JSON.stringify(pin));
}

/**
 * Reads the pin once and clears it. Callers must consume on every completed auth
 * resolution, signed in or not — a pin left behind by an abandoned switch would
 * otherwise be compared against a later, unrelated login.
 */
export function consumeWorkspaceSwitchPin(): WorkspaceSwitchPin | null {
  const parsed = parse<WorkspaceSwitchPin>(sessionStorage.getItem(WORKSPACE_SWITCH_PIN_KEY));
  sessionStorage.removeItem(WORKSPACE_SWITCH_PIN_KEY);
  if (!parsed) return null;

  const email = normalizeEmail(parsed.email);
  if (!email || typeof parsed.startedAt !== 'number') return null;
  if (Date.now() - parsed.startedAt > PIN_MAX_AGE_MS) return null;

  return {
    email,
    workspaceId: typeof parsed.workspaceId === 'string' ? parsed.workspaceId : '',
    startedAt: parsed.startedAt,
  };
}

/** An identity with no email cannot be verified, so it does not match. */
export function isPinnedIdentity(pin: WorkspaceSwitchPin, email: string | null | undefined): boolean {
  const actual = normalizeEmail(email);
  return actual !== null && actual === pin.email;
}

/** Survives the sign-out redirect so the login screen can explain itself. */
export function rememberBlockedWorkspaceSwitch(blocked: BlockedWorkspaceSwitch): void {
  sessionStorage.setItem(WORKSPACE_SWITCH_BLOCKED_KEY, JSON.stringify(blocked));
}

export function consumeBlockedWorkspaceSwitch(): BlockedWorkspaceSwitch | null {
  const parsed = parse<BlockedWorkspaceSwitch>(sessionStorage.getItem(WORKSPACE_SWITCH_BLOCKED_KEY));
  sessionStorage.removeItem(WORKSPACE_SWITCH_BLOCKED_KEY);
  if (!parsed) return null;

  const expectedEmail = normalizeEmail(parsed.expectedEmail);
  if (!expectedEmail) return null;
  return { expectedEmail, actualEmail: normalizeEmail(parsed.actualEmail) };
}
